import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  Renderer2,
  Input,
  Output,
  EventEmitter,
  HostListener
} from '@angular/core';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { GetInTouchComponent } from '../get-in-touch/get-in-touch.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  selector: 'app-personalize-plan',
  templateUrl: './personalize-plan.component.html',
  styleUrls: ['./personalize-plan.component.scss'],
  animations: [
    trigger('fadeAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('1300ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('1300ms ease-out', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class PersonalizePlanComponent implements AfterViewInit, OnDestroy {
  @ViewChild('gridStorySlider') gridStorySlider: ElementRef<HTMLElement>;
  @Input() optionASelected: boolean = false;
  @Input() optionBSelected: boolean = false;
  @Input() optionCSelected: boolean = false;
  @Output() closeCalculator = new EventEmitter<void>();

  slider3: KeenSliderInstance = null;
  selectedCombination: string = ''; // This will hold the combination info (Growth + or Premium +)
  getInTouch: boolean = false;

  // Prices based on subscription type and selected options
  pricing = {
    annually: {
      A: 150000,
      B: 250000,
      C: 175000,
      discount: '15% Disc',
      discountPercentage: 0,
      total: 0,
    },
    quarterly: {
      A: 168000,
      B: 280000,
      C: 196000,
      discount: '5% Disc',
      discountPercentage: 0,
      total: 0,
    },
    monthly: {
      A: 175000,
      B: 294000,
      C: 205000,
      discount: 'No Disc',
      discountPercentage: 0,
      total: 0,
    },
  };

  // Default subscription type
  subscriptionType: string = 'annually';

  // Current selected prices and discount text
  currentPrices = this.pricing[this.subscriptionType];

  // Set the subscription type and update prices
  setSubscription(type: string): void {
    this.subscriptionType = type;
    this.currentPrices = this.pricing[type];
    this.calculateTotalPrice();
  }

  // Handle the option selection and ensure A + B is not selected together
  toggleOption(option: string): void {
    if (option === 'A') {
      this.optionASelected = !this.optionASelected;
      if (this.optionASelected) this.optionBSelected = false; // Ensure A and B cannot be selected together
    } else if (option === 'B') {
      this.optionBSelected = !this.optionBSelected;
      if (this.optionBSelected) this.optionASelected = false; // Ensure A and B cannot be selected together
    } else if (option === 'C') {
      this.optionCSelected = !this.optionCSelected;
    }
    this.calculateTotalPrice();
  }

  // Calculate the total price based on the selected options and subscription type
  calculateTotalPrice(): void {
    let total = 0;
    if (this.optionASelected) total += this.currentPrices.A;
    if (this.optionBSelected) total += this.currentPrices.B;
    if (this.optionCSelected) total += this.currentPrices.C;

    // Apply discount based on subscription type
    total -= total * this.currentPrices.discountPercentage;
    this.currentPrices.total = total;

    // Set selected combination (Growth + or Premium +)
    if (this.optionASelected && !this.optionBSelected && !this.optionCSelected) {
      this.selectedCombination = 'Growth+';  // Only A is selected
    } else if (this.optionBSelected && this.optionCSelected) {
      this.selectedCombination = 'Premium+';  // B and C are selected
    } else {
      this.selectedCombination = '';  // No valid combination
    }
  }

  constructor(private renderer: Renderer2, private modalService: NgbModal) { }

  ngAfterViewInit() {
    if (this.gridStorySlider?.nativeElement) {
      this.slider3 = new KeenSlider(this.gridStorySlider.nativeElement, {
        breakpoints: {
          '(max-width: 1650px)': {
            slides: { perView: 2, spacing: 14 },
          },
          '(max-width: 1199px)': {
            slides: { perView: 2, spacing: 14 },
          },
          '(max-width: 767px)': {
            slides: { perView: 1, spacing: 16 },
          },
        },
        slides: { perView: 2, spacing: 24 },
      });
    }
  }

  updateActiveClass(sliderElement: HTMLElement, activeIndex: number) {
    const slides = sliderElement.querySelectorAll('.keen-slider__slide');
    slides.forEach((slide, index) => {
      if (index === activeIndex) {
        this.renderer.addClass(slide, 'active');
      } else {
        this.renderer.removeClass(slide, 'active');
      }
    });
  }

  ngOnDestroy() {
    if (this.slider3) this.slider3.destroy();
  }

  ngOnInit(): void {
    if (this.optionASelected) {
      this.optionASelected = true
    } else {
      this.optionBSelected = true
      this.optionCSelected = true
    }
    this.calculateTotalPrice();
  }

  openGetInTouchModal(planType: 'growth' | 'premium') {
    const modalRef = this.modalService.open(GetInTouchComponent, {
      backdropClass: 'primary-backdrop',
      windowClass: 'project-modal custom-form-modal',
      centered: true,
      backdrop: 'static', // Prevent closing on backdrop click
      keyboard: false // Prevent closing on ESC key
    });
    
    const planDetails = planType === 'growth' 
      ? `Growth+ Plan (${this.subscriptionType}): ${this.currentPrices.A}` 
      : `Premium+ Plan (${this.subscriptionType}): ${this.currentPrices.B + (this.optionCSelected ? this.currentPrices.C : 0)}`;
    
    modalRef.componentInstance.planDetails = planDetails;
    
    // Handle modal close
    modalRef.result.then(
      (result) => {
        // Modal closed with a result
        // Keep the calculator open
      },
      (reason) => {
        // Modal dismissed
        // Keep the calculator open
      }
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Get the clicked element
    const clickedElement = event.target as HTMLElement;
    
    // Check if the click is on a button or modal
    const isButton = clickedElement.closest('.btn');
    const isModal = clickedElement.closest('.modal');
    const isModalBackdrop = clickedElement.classList.contains('modal-backdrop');
    
    // If click is on a button or modal, don't close the calculator
    if (isButton || isModal || isModalBackdrop) {
      return;
    }
    
    // Check if the click is outside the pricing cards
    const starterKit = document.querySelector('.plan-card1');
    const growthKit = document.querySelector('.plan-card2');
    const premiumKit = document.querySelector('.plan-card3');
    
    if (starterKit && growthKit && premiumKit) {
      const isClickInsideCards = 
        starterKit.contains(clickedElement) ||
        growthKit.contains(clickedElement) ||
        premiumKit.contains(clickedElement);
        
      if (!isClickInsideCards) {
        this.closeCalculator.emit();
      }
    }
  }
}
